import { Injectable, Injector } from '@angular/core';

import { LoggingService } from '../../logging/logic/logging.service';
import { ActionHandler, ActionHandlerType } from '../action-handler';
import { SyncService } from '../../sync/sync.service';
import { DynFormsService } from '../../dyn-forms/logic/dyn-forms.service';
import { DraftSyncEventsService } from '../../dyn-forms/draft-sync-events.service';
import { DraftPushNotifier, DraftPushParams } from './draft-push-notifier.service';

/**
 * KRK-45: the user deleted this document on another one of their devices - "weg heisst
 * ueberall weg", so the local copy goes too.
 *
 * Deliberately WITHOUT a notification, which is the difference to draft_finalized: the user
 * deleted it themselves seconds ago, being told about it on their second phone is noise. A
 * form or list that is open right now reacts through draftDeleted$ instead.
 *
 * The cleanup does not wait for the sync: a deleted chain drops out of
 * syncactivityconfirmations entirely, so this push is the only reliable trigger.
 *
 * Unlike draft_finalized this also removes a locally EDITED (dirty) row. There is nothing
 * left for its queued upload to attach to - the backend stores it as a deleted conflict
 * version and answers isdraftdeleted - so keeping the row would only promise the user work
 * that can no longer go anywhere.
 */
@Injectable({
  providedIn: 'root',
})
export class DraftDeletedActionHandler implements ActionHandler {
  public readonly name: ActionHandlerType = 'draft_deleted';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private syncService: SyncService,
    private draftPushNotifier: DraftPushNotifier,
    private draftSyncEvents: DraftSyncEventsService,
    private injector: Injector,
  ) {}

  // ActionHandler
  public async executeAction(params?: DraftPushParams): Promise<any> {
    const chainRootAcId = this.draftPushNotifier.getChainRootAcId(params);
    const acukey = this.draftPushNotifier.getAcukey(params);
    const acId = this.draftPushNotifier.getAcId(params);
    this.logger.log(
      this.constructor.name,
      'Draft deleted on another device - chainRootAcId',
      chainRootAcId,
      'acukey',
      acukey,
      'acId',
      acId,
    );

    if (chainRootAcId == undefined && acukey == undefined) {
      this.logger.warn(
        this.constructor.name,
        'draft_deleted without chainrootacid and without acukey (acId',
        acId,
        ') - cannot identify the local rows, skipping cleanup',
      );
      return;
    }

    try {
      await this.injector
        .get(DynFormsService)
        .deleteDraftsForChain(chainRootAcId, acukey);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Failed to delete the local rows of chain',
        chainRootAcId,
        acukey,
        error?.message,
      );
    }

    // Tell an open form or draft list before the sync, so it stops showing the entry right
    // away instead of after the round trip.
    this.draftSyncEvents.draftDeleted$.next({ acId, acukey, chainRootAcId });

    try {
      await this.syncService.syncActivityConfirmations(true);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Sync of activity confirmations failed after a draft deletion',
        error?.message,
      );
    }
  }
}
